import { useEventListener } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { toBoolean } from "#ce/utils/props";
import { hasSlotContent } from "#ce/utils/slots";

/**
 * Whether the activator shows its caret, and whether the caret points at an open list. The caret
 * shows while there are items, unless the `append` slot has content, which replaces it. The slot is
 * read through the flattened assignment, so a forwarding slot with nothing behind it counts as empty.
 *
 * @param {object} props  Owning component's reactive props; reads `noCaretFlip`.
 * @param {{ appendSlot: import("vue").Ref<HTMLSlotElement | null>, hasItems: import("vue").Ref<boolean>,
 *   menuShown: import("vue").Ref<boolean> }} options
 * @returns {{ showsCaret: import("vue").ComputedRef<boolean>, caretActive: import("vue").ComputedRef<boolean>,
 *   hasTrailingContent: import("vue").ComputedRef<boolean> }}
 */
export function useAppendCaret(props, { appendSlot, hasItems, menuShown }) {
  const hasAppendContent = ref(false);

  useEventListener(appendSlot, "slotchange", readAppendContent);
  watch(appendSlot, readAppendContent, { immediate: true, flush: "post" });

  const caretActive = computed(() => menuShown.value && !toBoolean(props.noCaretFlip));
  const hasTrailingContent = computed(() => hasItems.value || hasAppendContent.value);
  const showsCaret = computed(() => hasItems.value && !hasAppendContent.value);

  function readAppendContent() {
    hasAppendContent.value = hasSlotContent(appendSlot.value);
  }

  return {
    showsCaret,
    caretActive,
    hasTrailingContent,
  };
}
